/**
 * Responsive Plugin CSS
 *
 * Card layout styles for narrow viewports.
 * Applied when [data-responsive] attribute is present on the grid.
 *
 * IMPORTANT: This CSS intentionally preserves the grid's virtualization system.
 * The row height observer will detect the new card height automatically and
 * recalculate the virtual window. Do NOT break virtualization by:
 * - Changing .rows-viewport overflow
 * - Changing .rows position
 * - Hiding .faux-vscroll
 */

/* ============================================
   Animation / Transition Support
   ============================================ */

/* View transition path: the compositor cross-fades the whole grid (or, with
   `animation: 'morph-rows' | 'morph-cells'`, each row or cell) between the table
   and card layouts. While this is active the plugin withholds
   `data-responsive-animate`, so the keyframe fade below never runs alongside it. */
tbw-grid[data-responsive-transition]::view-transition-group(*) {
  animation-duration: var(--tbw-responsive-duration, 200ms);
}

/* `morph-cells` names every cell individually. One `view-transition-class` keeps
   that a single style rule instead of one rule per generated name — the class
   does NOT capture anything on its own, the plugin still authors the names.
   Cells travel a real distance between the two layouts, so they get an
   ease-in-out curve rather than the root's cross-fade. */
tbw-grid[data-responsive-transition] .data-grid-row > .cell {
  view-transition-class: tbw-responsive-cell;
}

tbw-grid[data-responsive-transition]::view-transition-group(.tbw-responsive-cell) {
  animation-timing-function: ease-in-out;
}

/* A cell's box grows from one column to a full card row, and the UA stretches
   both snapshots to fill that box — so the text visibly zooms while it travels.
   Drawing them at natural size keeps the glyphs the size they actually are at
   both ends; the group still animates the box, so the cell still moves.
   Anchoring right lands the value on the card's right-aligned value slot rather
   than on the `Header:` label, and lets the label reveal as the box widens. */
tbw-grid[data-responsive-transition]::view-transition-old(.tbw-responsive-cell),
tbw-grid[data-responsive-transition]::view-transition-new(.tbw-responsive-cell) {
  width: 100%;
  height: 100%;
  object-fit: none;
  object-position: right center;
}

/* The old bitmap holds its value left-aligned in a column, the new one holds it
   right-aligned in a card — so the two can never line up exactly, and the gap
   varies with the text length. Front-load the cross-fade instead: the swap is
   over while the boxes are still visibly apart, and the rest of the travel runs
   on a single crisp copy. */
tbw-grid[data-responsive-transition]::view-transition-old(.tbw-responsive-cell),
tbw-grid[data-responsive-transition]::view-transition-new(.tbw-responsive-cell) {
  animation-duration: calc(var(--tbw-responsive-duration, 200ms) * 0.35);
}

/* Base transition setup for animated mode switching */
tbw-grid[data-responsive-animate] {
  .data-grid-row,
  .data-grid-row > .cell {
    transition:
      opacity var(--tbw-responsive-duration, 200ms) ease-out,
      transform var(--tbw-responsive-duration, 200ms) ease-out;
  }
}

/* Fade-in when entering responsive mode */
tbw-grid[data-responsive][data-responsive-animate] {
  .data-grid-row {
    animation: responsive-card-enter var(--tbw-responsive-duration, 200ms) ease-out;
  }
}

@keyframes responsive-card-enter {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============================================
   Responsive Card Layout
   ============================================ */

tbw-grid[data-responsive] {
  /* Hide entire header section in card mode */
  .header {
    display: none !important;
  }

  /* Hide footer row in card mode (aggregations don't make sense in cards) */
  .footer-row {
    display: none;
  }

  /* Override horizontal scroll - single column flow */
  .tbw-scroll-area {
    overflow-x: hidden;
    min-width: 0 !important;
  }

  /* Override rows-body-wrapper to allow natural flow */
  .rows-body-wrapper {
    min-width: 0 !important;
  }

  /* Convert row from CSS Grid to block layout */
  .data-grid-row:not(.group-row) {
    display: block !important;
    grid-template-columns: none !important;
    padding: var(--tbw-cell-padding);
    padding-inline-start: var(--tbw-spacing-xl);
    border-bottom: 1px solid var(--tbw-color-border);
    /* Disable containment so row can grow to fit stacked cells */
    contain: none !important;
    content-visibility: visible !important;
    background: var(--tbw-color-bg);
    position: relative;

    &:nth-child(even) {
      background: var(--tbw-color-row-alt);
    }

    &:hover {
      background: var(--tbw-color-row-hover);
    }

    &[aria-selected='true'] {
      background: var(--tbw-color-selection);

      /* Selection indicator bar */
      &::before {
        content: '';
        position: absolute;
        inset-inline-start: 0;
        top: 0;
        bottom: 0;
        width: 4px;
        background: var(--tbw-color-accent);
      }
    }
  }

  /* The default stacked-cell card grows to fit its cells. A custom `cardRenderer`
     row sets its own height from `cardRowHeight`, and a loading placeholder takes
     the card height the plugin reports for it, so neither may be forced open. */
  .data-grid-row:not(.group-row, .responsive-card, .placeholder-row) {
    min-height: auto !important;
    height: auto !important;
  }

  /* Stack cells vertically as label: value pairs. A placeholder's single spinner
     cell has no field, so it gets no `Header:` label. */
  .data-grid-row:not(.group-row, .placeholder-row) > .cell {
    display: flex !important;
    justify-content: space-between;
    align-items: center;
    padding: var(--tbw-spacing-xs) var(--tbw-spacing-md);
    width: 100% !important;
    min-width: 0 !important;
    min-height: auto !important;
    height: auto !important;
    line-height: 1.5 !important;
    position: static !important;
    left: auto !important;
    right: auto !important;
    border: none !important;
    border-bottom: none !important;
    border-inline-end: none !important;
    background: transparent !important;
    white-space: normal !important;
    overflow: visible !important;

    /* Header label from data-header attribute */
    &::before {
      content: attr(data-header) ': ';
      font-weight: 600;
      color: var(--tbw-color-header-fg);
      flex-shrink: 0;
      margin-inline-end: var(--tbw-spacing-md);
      min-width: 100px;
    }

    /* Value takes remaining space */
    &::after {
      content: none;
    }
  }

  /* Hide utility columns (row expander, selection checkbox, etc.) */
  .cell[data-utility] {
    display: none !important;
  }
}

/* Hidden columns — specificity must beat `.data-grid-row:not(.group-row) > .cell` above */
tbw-grid[data-responsive] .data-grid-row > .cell[data-responsive-hidden] {
  display: none !important;
}

/* Value-only columns in responsive mode (hides label, shows value full-width) */
tbw-grid[data-responsive] .data-grid-row > .cell[data-responsive-value-only] {
  /* Override the header label ::before to hide it */
  &::before {
    display: none !important;
  }

  /* Value takes full width, no justify-content needed */
  justify-content: flex-start !important;
  font-weight: 500;
}

/* Opt-in: hide per-card field labels (the "Name:" prefix) when
   `hideHeader: true` is configured. Toggled by ResponsivePlugin. */
tbw-grid[data-responsive][data-responsive-hide-header] .data-grid-row:not(.group-row) > .cell::before {
  display: none !important;
}

/* Non-responsive mode: a hidden column collapses its grid track to zero width
   rather than leaving layout. The cell must stay in flow — cells are auto-placed,
   so dropping one would pull every later cell into the wrong track. Padding and
   border are zeroed too, or the cell would floor at ~17px and the track could
   never reach zero. Opacity is deliberately not `!important`: the plugin fades
   the changed cells with WAAPI, and `!important` outranks animations. */
tbw-grid:not([data-responsive]) .cell[data-responsive-hidden] {
  padding-inline: 0 !important;
  border-inline-width: 0 !important;
  overflow: hidden !important;
  pointer-events: none;
  opacity: 0;
}

/* `grid-template-columns` is animatable, and a row element survives a render
   (only its cells are rebuilt), so the collapse is a plain transition. Gated on
   the fade window so column resizing stays instant. A track list only
   interpolates pairwise, which holds here — collapsing never changes the number
   of tracks. */
tbw-grid[data-responsive-column-fade] :is(.header-row, .data-grid-row) {
  transition: grid-template-columns var(--tbw-responsive-duration, 200ms) ease-out;
}

/* Hide pinned/aggregation rows in responsive mode - they don't make sense in card layout */
tbw-grid[data-responsive] {
  .tbw-footer,
  .tbw-header-pinned,
  .tbw-pinned-rows,
  .tbw-aggregation-rows {
    display: none !important;
  }

  /* Reset min-widths that hold horizontal scroll width */
  .tbw-pinned-rows,
  .tbw-aggregation-rows,
  .tbw-aggregation-row {
    min-width: 0 !important;
  }
}

/* Custom card renderer styling */
tbw-grid[data-responsive] .data-grid-row.responsive-card {
  /* Override default cell layout - custom content takes full width */
  display: block !important;
  padding: var(--tbw-cell-padding);
  border-bottom: 1px solid var(--tbw-color-border);

  /* Card content should not have cell-based constraints */
  > * {
    width: 100%;
  }

  /* Remove default cell ::before headers - custom renderer handles layout */
  .cell::before {
    display: none;
  }
}
